// src/components/Survey/QuestionTypes/LikertScaleQuestion.js
import React, { useState, useEffect } from "react";
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio, TextField } from "@mui/material";
import { likertScaleTypes } from "../../../constants/likertScales";

function LikertScaleQuestion({ question, index, handleQuestionChange }) {
  // Find the selected scale type and its options
  const selectedScale = likertScaleTypes.find((scale) => scale.id === question.scaleType) || likertScaleTypes[0];
  const [editingIndex, setEditingIndex] = useState(null); // Track which option is being edited
  const [editingValue, setEditingValue] = useState(""); // Temporary value for editing
  const [customChoiceCount, setCustomChoiceCount] = useState(question.customChoiceCount || 5); // Default to 5 choices

  // Sync customChoiceCount when question prop changes
  useEffect(() => {
    if (question.customChoiceCount !== undefined) {
      setCustomChoiceCount(question.customChoiceCount);
    }
  }, [question.customChoiceCount]);

  // Handle scale type change
  const handleScaleTypeChange = (event) => {
    const newScaleType = event.target.value;
    handleQuestionChange(index, "scaleType", newScaleType);

    if (newScaleType === "CUSTOM") {
      // Initialize with default custom options based on customChoiceCount
      const count = question.customChoiceCount || customChoiceCount || 5;

      // Get default choices based on count
      const getDefaultChoices = (count) => {
        switch (count) {
          case 3:
            return [
              { text: "Disagree", value: "disagree", index: 0, tags: [] },
              { text: "Neutral", value: "neutral", index: 1, tags: [] },
              { text: "Agree", value: "agree", index: 2, tags: [] }
            ];
          case 5:
            return [
              { text: "Option 1", value: "option_1", index: 0, tags: [] },
              { text: "Option 2", value: "option_2", index: 1, tags: [] },
              { text: "Option 3", value: "option_3", index: 2, tags: [] },
              { text: "Option 4", value: "option_4", index: 3, tags: [] },
              { text: "Option 5", value: "option_5", index: 4, tags: [] }
            ];
          case 10:
            return Array.from({ length: 10 }, (_, i) => ({
              text: (i + 1).toString(),
              value: `option_${i + 1}`,
              index: i,
              tags: []
            }));
          default:
            return Array.from({ length: count }, (_, i) => ({
              text: `Option ${i + 1}`,
              value: `option_${i + 1}`,
              index: i,
              tags: []
            }));
        }
      };

      if (!question.choices || question.choices.length === 0) {
        const defaultCustomOptions = getDefaultChoices(count);
        handleQuestionChange(index, "choices", defaultCustomOptions);
        handleQuestionChange(index, "customChoiceCount", count);
      }
    } else {
      // For non-custom scales, reset choices to predefined options
      const selectedScaleType = likertScaleTypes.find((scale) => scale.id === newScaleType);
      if (selectedScaleType) {
        const choices = selectedScaleType.options.map((option, idx) => ({
          text: option,
          value: option.toLowerCase().replace(/\s+/g, '_'),
          index: idx,
          tags: []
        }));
        handleQuestionChange(index, "choices", choices);
      }
    }
  };

  // Handle custom choice count change (3, 5, or 10)
  const handleCustomChoiceCountChange = (event) => {
    const count = parseInt(event.target.value);
    setCustomChoiceCount(count);

    // Get default choices based on count
    const getDefaultChoices = (count) => {
      switch (count) {
        case 3:
          return [
            { text: "Disagree", value: "disagree", index: 0, tags: [] },
            { text: "Neutral", value: "neutral", index: 1, tags: [] },
            { text: "Agree", value: "agree", index: 2, tags: [] }
          ];
        case 5:
          return [
            { text: "Strongly Disagree", value: "strongly_disagree", index: 0, tags: [] },
            { text: "Disagree", value: "disagree", index: 1, tags: [] },
            { text: "Neutral", value: "neutral", index: 2, tags: [] },
            { text: "Agree", value: "agree", index: 3, tags: [] },
            { text: "Strongly Agree", value: "strongly_agree", index: 4, tags: [] }
          ];
        case 10:
          return Array.from({ length: 10 }, (_, i) => ({
            text: (i + 1).toString(),
            value: `option_${i + 1}`,
            index: i,
            tags: []
          }));
        default:
          return Array.from({ length: count }, (_, i) => ({
            text: `Option ${i + 1}`,
            value: `option_${i + 1}`,
            index: i,
            tags: []
          }));
      }
    };

    // Use default choices for the selected count
    const newChoices = getDefaultChoices(count);

    handleQuestionChange(index, "choices", newChoices);
    handleQuestionChange(index, "customChoiceCount", count);
  };


  // Handle editing an option
  const handleEditOption = (optionIndex, currentText) => {
    setEditingIndex(optionIndex);
    setEditingValue(currentText);
  };

  // Handle saving edited option
  const handleSaveEdit = (optionIndex) => {
    if (editingValue.trim() !== "") {
      const updatedChoices = [...(question.choices || [])];
      updatedChoices[optionIndex] = {
        ...updatedChoices[optionIndex],
        text: editingValue.trim(),
        value: editingValue.trim().toLowerCase().replace(/\s+/g, '_')
      };
      handleQuestionChange(index, "choices", updatedChoices);
    }
    setEditingIndex(null);
    setEditingValue("");
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue("");
  };

  // Determine the options to display
  const displayOptions = question.choices && question.choices.length > 0
    ? question.choices.map(choice => choice.text)
    : selectedScale.options;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontSize: "0.875rem", color: "text.secondary" }}>
        Select Likert Scale Type
      </Typography>
      <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 3 }}>
        <InputLabel>Scale Type</InputLabel>
        <Select
          value={question.scaleType || "AGREEMENT"}
          onChange={handleScaleTypeChange}
          label="Scale Type"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              backgroundColor: "#fff",
            },
          }}
        >
          {likertScaleTypes.map((scale) => (
            <MenuItem key={scale.id} value={scale.id}>
              {scale.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {question.scaleType === "CUSTOM" && (
        <Box sx={{ mb: 3 }}>
          {/* Number of Choices Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1.5, fontSize: "0.875rem", color: "text.secondary" }}>
              Select Number of Choices
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={customChoiceCount}
                onChange={handleCustomChoiceCountChange}
              >
                <FormControlLabel
                  value={3}
                  control={<Radio />}
                  label="3 Choices"
                  sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                />
                <FormControlLabel
                  value={5}
                  control={<Radio />}
                  label="5 Choices"
                  sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                />
                <FormControlLabel
                  value={10}
                  control={<Radio />}
                  label="10 Choices"
                  sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Editable Choices */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 1.5 }}>
              Customize Choice Labels (click to edit)
            </Typography>
          </Box>

          {question.choices && question.choices.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {question.choices.map((choice, optionIndex) => (
                <Box key={optionIndex} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                  {/* Choice Number */}
                  <Typography variant="body2" sx={{ minWidth: "30px", fontWeight: "medium", color: "text.secondary" }}>
                    {optionIndex + 1}.
                  </Typography>

                  {/* Editable text */}
                  <Box sx={{ flexGrow: 1 }}>
                    {editingIndex === optionIndex ? (
                      <TextField
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={() => handleSaveEdit(optionIndex)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveEdit(optionIndex);
                          } else if (e.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                        size="small"
                        fullWidth
                        autoFocus
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        onClick={() => handleEditOption(optionIndex, choice.text)}
                        sx={{
                          cursor: "pointer",
                          p: 1,
                          borderRadius: "4px",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        {choice.text}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Typography variant="body2" sx={{ mb: 1, fontSize: "0.875rem", color: "text.secondary" }}>
        Scale Options Preview
      </Typography>
      {displayOptions.length > 0 ? (
        <FormControl component="fieldset">
          <RadioGroup row>
            {displayOptions.map((option, optionIndex) => (
              <FormControlLabel
                key={optionIndex}
                value={option}
                control={<Radio disabled size="small" />}
                label={option}
                sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {question.scaleType === "CUSTOM"
            ? "Add at least one custom option to see the preview."
            : "No options available for this scale type."}
        </Typography>
      )}
    </Box>
  );
}

export default LikertScaleQuestion;
